import React, { useState } from 'react';
import { Filter, X, Search, DollarSign, Bed, Bath, Square, Calendar, Home, MapPin } from 'lucide-react';

const PropertyFilters = ({ onFiltersChange, totalProperties = 0, filteredCount = 0 }) => {
  const [filters, setFilters] = useState({
    priceMin: '',
    priceMax: '',
    bedrooms: '',
    bathrooms: '',
    sqftMin: '',
    sqftMax: '',
    yearBuiltMin: '',
    yearBuiltMax: '',
    propertyType: '',
    hasGarage: '',
    hasPool: '',
    hasFireplace: '',
    distance: '',
    daysOnMarket: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      priceMin: '',
      priceMax: '',
      bedrooms: '',
      bathrooms: '',
      sqftMin: '',
      sqftMax: '',
      yearBuiltMin: '',
      yearBuiltMax: '',
      propertyType: '',
      hasGarage: '',
      hasPool: '',
      hasFireplace: '',
      distance: '',
      daysOnMarket: ''
    };
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const removeFilter = (filterName) => {
    handleFilterChange(filterName, '');
  };

  const getFilterDisplayName = (filterName, value) => {
    switch (filterName) {
      case 'priceMin': return `Min Price: $${parseInt(value).toLocaleString()}`;
      case 'priceMax': return `Max Price: $${parseInt(value).toLocaleString()}`;
      case 'bedrooms': return `${value} Bedrooms`;
      case 'bathrooms': return `${value} Bathrooms`;
      case 'sqftMin': return `Min: ${parseInt(value).toLocaleString()} sq ft`;
      case 'sqftMax': return `Max: ${parseInt(value).toLocaleString()} sq ft`;
      case 'yearBuiltMin': return `Built after ${value}`;
      case 'yearBuiltMax': return `Built before ${value}`;
      case 'propertyType': return `Type: ${value}`;
      case 'hasGarage': return 'Has Garage';
      case 'hasPool': return 'Has Pool';
      case 'hasFireplace': return 'Has Fireplace';
      case 'distance': return `Within ${value} mi`;
      case 'daysOnMarket': return `${value} days on market`;
      default: return value;
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">
            Filter Comparable Properties
          </h3>
          <span className={`text-sm ${filteredCount === 0 && totalProperties > 0 ? 'text-red-400 font-semibold' : 'text-gray-400'}`}>
            ({filteredCount} of {totalProperties} properties)
            {filteredCount === 0 && totalProperties > 0 && getActiveFiltersCount() > 0 && (
              <span className="ml-2 text-red-400">⚠️ All filtered out!</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={clearAllFilters}
              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
            >
              Clear All ({getActiveFiltersCount()})
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Active Filters Tags */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(filters).map(([key, value]) => {
            if (value === '') return null;
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-full"
              >
                {getFilterDisplayName(key, value)}
                <button
                  onClick={() => removeFilter(key)}
                  className="hover:bg-blue-700 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Filter Controls */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Price Range */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Price Range
            </h4>
            <div className="space-y-2">
              <input
                type="number"
                placeholder="Min Price"
                value={filters.priceMin}
                onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                placeholder="Max Price"
                value={filters.priceMax}
                onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Bedrooms & Bathrooms */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <Bed className="w-4 h-4" />
              Beds & Baths
            </h4>
            <div className="space-y-2">
              <select
                value={filters.bedrooms}
                onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Bedrooms</option>
                <option value="1">1 Bedroom</option>
                <option value="2">2 Bedrooms</option>
                <option value="3">3 Bedrooms</option>
                <option value="4">4 Bedrooms</option>
                <option value="5">5+ Bedrooms</option>
              </select>
              <select
                value={filters.bathrooms}
                onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Bathrooms</option>
                <option value="1">1+ Bathroom</option>
                <option value="1.5">1.5+ Bathrooms</option>
                <option value="2">2+ Bathrooms</option>
                <option value="2.5">2.5+ Bathrooms</option>
                <option value="3">3+ Bathrooms</option>
                <option value="4">4+ Bathrooms</option>
              </select>
            </div>
          </div>

          {/* Square Footage */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <Square className="w-4 h-4" />
              Square Footage
            </h4>
            <div className="space-y-2">
              <input
                type="number"
                placeholder="Min Sq Ft"
                value={filters.sqftMin}
                onChange={(e) => handleFilterChange('sqftMin', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                placeholder="Max Sq Ft"
                value={filters.sqftMax}
                onChange={(e) => handleFilterChange('sqftMax', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Year Built */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Year Built
            </h4>
            <div className="space-y-2">
              <input
                type="number"
                placeholder="Built After"
                value={filters.yearBuiltMin}
                onChange={(e) => handleFilterChange('yearBuiltMin', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="number"
                placeholder="Built Before"
                value={filters.yearBuiltMax}
                onChange={(e) => handleFilterChange('yearBuiltMax', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Property Type */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <Home className="w-4 h-4" />
              Property Type
            </h4>
            <select
              value={filters.propertyType}
              onChange={(e) => handleFilterChange('propertyType', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Any Type</option>
              <option value="Single Family Residence">Single Family</option>
              <option value="Townhouse">Townhouse</option>
              <option value="Condominium">Condominium</option>
              <option value="Multi-Family">Multi-Family</option>
            </select>
          </div>

          {/* Distance */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Distance
            </h4>
            <select
              value={filters.distance}
              onChange={(e) => handleFilterChange('distance', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Any Distance</option>
              <option value="0.5">Within 0.5 miles</option>
              <option value="1">Within 1 mile</option>
              <option value="2">Within 2 miles</option>
              <option value="5">Within 5 miles</option>
            </select>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300">Features</h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={filters.hasGarage === 'true'}
                  onChange={(e) => handleFilterChange('hasGarage', e.target.checked ? 'true' : '')}
                  className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Has Garage</span>
              </label>
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={filters.hasPool === 'true'}
                  onChange={(e) => handleFilterChange('hasPool', e.target.checked ? 'true' : '')}
                  className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Has Pool</span>
              </label>
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={filters.hasFireplace === 'true'}
                  onChange={(e) => handleFilterChange('hasFireplace', e.target.checked ? 'true' : '')}
                  className="rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Has Fireplace</span>
              </label>
            </div>
          </div>

          {/* Days on Market */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-blue-300">Market Activity</h4>
            <select
              value={filters.daysOnMarket}
              onChange={(e) => handleFilterChange('daysOnMarket', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Any DOM</option>
              <option value="0-30">0-30 days</option>
              <option value="31-60">31-60 days</option>
              <option value="61-90">61-90 days</option>
              <option value="91-120">91-120 days</option>
              <option value="120+">120+ days</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyFilters;